import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import InputArea from '../InputArea'
import Attachments from '../Attachments'
import InputControls from '../InputControls'

import { transformToEmoji } from '@SonarCX/utilities/stringToEmoji'
import {
  TYPES,
  selectChatBox,
  clearData,
  updateValue,
  clearUniqueAlerts
} from '@SonarCX/slices/chatBoxSlice'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useCanSend } from '@SonarCX/providers/CanSendProvider'
import { useMediaLoaded } from '@SonarCX/providers/MediaLoadedProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useEnterMode } from '@SonarCX/providers/EnterModeProvider'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useChatScroll } from '@SonarCX/providers/ChatScrollProvider'

import useStyles from './styles'

const SPACE_KEYCODE = 32
const UNDO_KEYCODE = 90

const Input = () => {
  const dispatch = useAppDispatch()
  const { currentID, values } = useAppSelector(selectChatBox)

  const { id: selectedConversation, conversation } = useQueueState()
  const { canSend, setCanSend } = useCanSend()
  const { doneUploading } = useMediaLoaded()
  const {
    dashboard: { error: dashboardError },
    api: { typeHandler, messageHandler, noteHandler, throttledTypingEvent }
  } = useSocket()
  const {
    state: editState,
    start: startEdit,
    update: updateEdit,
    end: endEdit,
    submit: submitEdit
  } = useChatEditState()
  const { sendOnEnter, temp, enableTemp, resetTemp } = useEnterMode()
  const hasPermission = useAccess()
  const { deleted: ownerDeleted } = useOwner()
  const { user } = useEnvironment()
  const { scrollToBottom } = useChatScroll()

  const classes = useStyles({ isPrivate: values.isPrivate })

  const canAddNotes = React.useMemo(
    () => hasPermission(PERMS.NotesFull),
    [hasPermission]
  )

  const inputDisabled = React.useMemo(() => {
    if (values.isPrivate) {
      return !canAddNotes
    }

    const isYours =
      conversation.users?.includes(user?.id) ||
      sessionStorage.getItem('viewing') === selectedConversation

    return !isYours || conversation.closed || ownerDeleted || !canSend
  }, [
    values.isPrivate,
    canAddNotes,
    conversation,
    user,
    selectedConversation,
    ownerDeleted,
    canSend
  ])

  // Clear alerts on switching type
  React.useEffect(() => {
    if (values.type !== TYPES.SMS) {
      dispatch(clearUniqueAlerts('phone'))
    }

    if (values.type !== TYPES.EMAIL) {
      dispatch(clearUniqueAlerts('email'))
    }
  }, [values.type, dispatch])

  // Enables sending when phone, email, or type changes
  React.useEffect(() => {
    if (values.type === TYPES.LIVE) {
      setCanSend(true)
    }
  }, [currentID, values.type, setCanSend, values.phones, values.emails])

  // Switch to notes when convo is closed
  React.useEffect(() => {
    if (conversation?.closed && conversation.session === currentID) {
      if (!values.isPrivate) {
        dispatch(updateValue('isPrivate', true))
      }
      if (values.type !== TYPES.LIVE) {
        dispatch(updateValue('type', TYPES.LIVE))
      }
    }
  }, [conversation, values.isPrivate, values.type, currentID, dispatch])

  // Change send state when files are uploading
  React.useEffect(() => {
    if (!doneUploading) {
      setCanSend(false)
    }

    // clear message after data is uploaded
    if (!dashboardError) {
      dispatch(clearData())
      setCanSend(true)
    }
  }, [doneUploading, dispatch, dashboardError, setCanSend])

  // Sync type with stored conversation type
  React.useEffect(() => {
    let newType = 'Live'

    switch (values.type) {
      case TYPES.SMS:
        newType = 'SMS'
        break
      case TYPES.EMAIL:
        newType = 'Email'
        break
      case TYPES.LIVE:
      default:
        newType = 'Live'
        break
    }

    typeHandler(selectedConversation, newType, false)
  }, [values.type, typeHandler, selectedConversation])

  // Focus input on clicking the surrounding area
  const handleClick = React.useCallback(() => {
    const input = document.querySelector('#chat-input')
    input?.click && input.click()
  }, [])

  // Sends a message through the socket
  const handleMessageSubmit = React.useCallback(
    async (message, file) => {
      await messageHandler(
        message,
        'Send',
        file,
        selectedConversation,
        false,
        true
      )
      scrollToBottom()
    },
    [messageHandler, selectedConversation, scrollToBottom]
  )

  const setMessage = React.useCallback(
    val => {
      dispatch(updateValue('message', val))
    },
    [dispatch]
  )

  const setHasUndoReplacedString = React.useCallback(
    val => {
      dispatch(updateValue('hasUndoReplacedString', val))
    },
    [dispatch]
  )

  const setUndoText = React.useCallback(
    val => {
      dispatch(updateValue('undoText', val))
    },
    [dispatch]
  )

  const setIsReplacedString = React.useCallback(
    val => {
      dispatch(updateValue('isReplacedString', val))
    },
    [dispatch]
  )

  // Submits a comment
  const handleCommentSubmit = React.useCallback(
    async (comment, file) => {
      await noteHandler(selectedConversation, comment, 'Send', file)
      scrollToBottom()
    },
    [noteHandler, selectedConversation, scrollToBottom]
  )

  // Responding to files growing text area and msg staying on bottom
  React.useEffect(() => {
    if (values.file?.length) {
      scrollToBottom()
    }
  }, [values.file, scrollToBottom])

  // Handles formatting a message for sending
  const sendMessage = React.useCallback(
    fromClick => {
      if (!canSend && !values.isPrivate) {
        return false
      }

      const { newText } = transformToEmoji(values.message)
      const value = (
        values.hasUndoReplacedString ? values.message.trim() : newText
      ).trim()
      if (value.length > 0 || values.file.length > 0) {
        resetTemp()

        if (!values.isPrivate) {
          let message = value
          if (values.type === TYPES.SMS) {
            message = { content: value, number: values.phones }
          } else if (values.type === TYPES.EMAIL) {
            message = {
              content: value,
              title: values.subject,
              email: values.emails
            }
          }

          handleMessageSubmit(message, values.file)
        } else {
          if (fromClick) {
            dispatch(updateValue('message', ''))
            handleCommentSubmit(value, values.file)
            dispatch(updateValue('file', []))
          }
        }

        dispatch(updateValue('hasUndoReplacedString', false))
      }

      return true
    },
    [
      canSend,
      values.isPrivate,
      values.message,
      values.hasUndoReplacedString,
      values.file,
      resetTemp,
      values.type,
      values.phones,
      values.subject,
      values.emails,
      dispatch,
      handleMessageSubmit,
      handleCommentSubmit
    ]
  )

  // Sends message if enter is pressed
  // Return false if you want the pressed key to be passed to the editor
  const checkSubmit = React.useCallback(
    e => {
      const { key, shiftKey, keyCode } = e
      if (key === 'Enter') {
        // Disable send on enter when shift is held once
        if (shiftKey) {
          enableTemp()
          return false
        }

        // Prevent sending when disabled
        if (!sendOnEnter || temp) {
          return false
        }

        // Emails should never send from key input
        const shouldSend = values.type !== TYPES.EMAIL && doneUploading

        if (shouldSend) {
          if (editState.editing) {
            e.preventDefault()
            submitEdit()
            resetTemp()
            return true
          } else {
            return sendMessage(true)
          }
        }
      } else {
        if (keyCode === SPACE_KEYCODE) {
          setHasUndoReplacedString(false)
          // if (type === 'Task') {
          //   const { newText, originalText, replaced } = transformToEmoji(
          //     taskVals.content
          //   )
          //   setUndoText(originalText)
          //   setIsReplacedString(replaced)
          //   setTaskVals(vals => ({ ...vals, content: newText }))
          // } else
          if (editState.editing) {
            const { newText, originalText, replaced } = transformToEmoji(
              editState.newMessage
            )
            setUndoText(originalText)
            setIsReplacedString(replaced)
            updateEdit(newText)
          } else {
            const { newText, originalText, replaced } = transformToEmoji(
              values.message
            )
            setUndoText(originalText)
            setIsReplacedString(replaced)
            setMessage(newText)
          }
        }
        if (
          keyCode === UNDO_KEYCODE &&
          (e.ctrlKey || e.metaKey) &&
          values.isReplacedString
        ) {
          // if (type === 'Task') {
          //   setTaskVals(vals => ({ ...vals, content: undoText }))
          // } else if (editState.editing) {
          //   updateEdit(undoText)
          // } else {
          //   setMessage(undoText)
          // }
          // setHasUndoReplacedString(true)
          // e.preventDefault()
        }
        if (key === 'ArrowUp') {
          if (values.message.trim().length === 0 && !editState.editing) {
            startEdit()
            e.preventDefault()
          }
        } else if (key === 'Escape') {
          endEdit()
          e.preventDefault()
        }
      }

      throttledTypingEvent(selectedConversation, false)
    },
    [
      throttledTypingEvent,
      selectedConversation,
      sendOnEnter,
      temp,
      values.type,
      values.isReplacedString,
      values.message,
      doneUploading,
      enableTemp,
      editState.editing,
      editState.newMessage,
      submitEdit,
      resetTemp,
      sendMessage,
      setHasUndoReplacedString,
      setUndoText,
      setIsReplacedString,
      updateEdit,
      setMessage,
      startEdit,
      endEdit
    ]
  )

  return (
    <div onClick={handleClick} className={classes.root}>
      <InputArea inputDisabled={inputDisabled} checkSubmit={checkSubmit} />

      {values.file?.length > 0 && (
        <div className={classes.holder}>
          <Attachments />
        </div>
      )}

      <InputControls inputDisabled={inputDisabled} sendMessage={sendMessage} />
    </div>
  )
}

export default Input
